import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { api, type MailAttachmentPayload, type MailMessageDetail, type PublicMailAccount } from "../../lib/api";
import { useConfirm } from "../shell/ConfirmDialog";
import { RichMailEditor } from "./RichMailEditor";

export type ComposeMode = "new" | "reply" | "replyAll" | "forward" | "draft";

function extractEmails(raw: string | undefined | null): string[] {
  if (!raw) return [];
  const found = raw.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) || [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const e of found) {
    const key = e.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out;
}

function escapeHtml(s: string) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function prefixSubject(subject: string, prefix: "Re:" | "Fwd:") {
  const s = (subject || "").trim();
  if (!s) return `${prefix} `;
  if (prefix === "Re:" && /^re:/i.test(s)) return s;
  if (prefix === "Fwd:" && /^(fwd|fw):/i.test(s)) return s;
  return `${prefix} ${s}`;
}

/** Raw body of the message being replied to / forwarded. */
function messageBodyHtml(source: MailMessageDetail): string {
  const html = (source.html || "").trim();
  const text = (source.text || "").trim();
  const textAsHtml = text
    ? `<div style="white-space:pre-wrap;font-family:inherit">${escapeHtml(text).replace(/\n/g, "<br/>")}</div>`
    : "";
  if (html && textAsHtml) {
    const htmlPlainLen = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().length;
    if (text.length > htmlPlainLen * 1.8) return textAsHtml;
    return html;
  }
  if (html) return html;
  return textAsHtml || "<p>(messaggio vuoto)</p>";
}

/**
 * Sanitize HTML so TipTap keeps a readable quote tree (blockquote nesting).
 * Existing blockquotes in the original mail stay nested → botta e risposta.
 */
function sanitizeQuoteInner(html: string): string {
  let clean = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "style", "link", "meta", "svg", "math"],
    ADD_ATTR: ["target", "rel"],
  });
  // TipTap prefers <p> over bare <div>; unwrap empty wrappers lightly
  clean = clean
    .replace(/<div(\s[^>]*)?>/gi, "<p>")
    .replace(/<\/div>/gi, "</p>")
    .replace(/<p>\s*<\/p>/gi, "");
  if (!clean.trim()) return "<p></p>";
  // Ensure blockquote children are blocks (TipTap blockquote = block+)
  if (!/<(p|blockquote|ul|ol|h[1-3]|pre|hr)\b/i.test(clean)) {
    return `<p>${clean}</p>`;
  }
  return clean;
}

/**
 * Classic mail quote: attribution line + blockquote (nests prior quotes).
 * This is what lands inside the rich-text editor.
 */
function buildQuotedBlock(mode: ComposeMode, source: MailMessageDetail): string {
  const when = source.date ? new Date(source.date).toLocaleString() : "";
  const from = escapeHtml(source.from || "");
  const inner = sanitizeQuoteInner(messageBodyHtml(source));

  if (mode === "forward") {
    return [
      `<p><strong>---------- Messaggio inoltrato ----------</strong></p>`,
      `<p><strong>Da:</strong> ${from}<br>`,
      `<strong>Data:</strong> ${escapeHtml(when)}<br>`,
      `<strong>Oggetto:</strong> ${escapeHtml(source.subject || "")}<br>`,
      `<strong>A:</strong> ${escapeHtml(source.to || "")}`,
      source.cc ? `<br><strong>Cc:</strong> ${escapeHtml(source.cc)}` : "",
      `</p>`,
      `<blockquote>${inner}</blockquote>`,
    ].join("");
  }

  return [
    `<p><em>Il ${escapeHtml(when)} ${from} ha scritto:</em></p>`,
    `<blockquote>${inner}</blockquote>`,
  ].join("");
}

/** Full editor document: blank reply space + quoted history. */
function buildEditorInitialHtml(mode: ComposeMode, source: MailMessageDetail | null): string {
  if (!source || mode === "new") return "<p></p>";
  if (mode === "draft") return sanitizeQuoteInner(messageBodyHtml(source));
  // Two blank paragraphs so the caret starts above the quote (Gmail-like)
  return `<p></p><p></p>${buildQuotedBlock(mode, source)}`;
}

function buildComposeDraft(
  mode: ComposeMode,
  source: MailMessageDetail | null,
  selfEmails: string[],
): {
  to: string;
  cc: string;
  subject: string;
  title: string;
} {
  const self = new Set(selfEmails.map((e) => e.toLowerCase()));
  if (!source || mode === "new") {
    return { to: "", cc: "", subject: "", title: "Nuovo messaggio" };
  }

  if (mode === "draft") {
    return {
      to: source.to || "",
      cc: source.cc || "",
      subject: (source.subject || "").trim(),
      title: "Modifica bozza",
    };
  }

  if (mode === "forward") {
    return {
      to: "",
      cc: "",
      subject: prefixSubject(source.subject, "Fwd:"),
      title: "Inoltra",
    };
  }

  const fromEmails = extractEmails(source.from);
  const toEmails = extractEmails(source.to);
  const ccEmails = extractEmails(source.cc);

  if (mode === "reply") {
    return {
      to: fromEmails.join(", "),
      cc: "",
      subject: prefixSubject(source.subject, "Re:"),
      title: "Rispondi",
    };
  }

  const primary = fromEmails[0] ? [fromEmails[0]] : [];
  const ccSet = new Set<string>();
  for (const e of [...fromEmails.slice(1), ...toEmails, ...ccEmails]) {
    const key = e.toLowerCase();
    if (self.has(key)) continue;
    if (primary.some((p) => p.toLowerCase() === key)) continue;
    ccSet.add(e);
  }
  return {
    to: primary.join(", "),
    cc: [...ccSet].join(", "),
    subject: prefixSubject(source.subject, "Re:"),
    title: "Rispondi a tutti",
  };
}

function draftStorageKey(accountId: string, mode: ComposeMode, source: MailMessageDetail | null) {
  // v3: quote lives inside bodyHtml (invalidates older local drafts without quote)
  return `mailstorm.draft.v3.${accountId}.${mode}.${source?.accountId || "new"}.${source?.uid || 0}`;
}

function bodyHasQuote(html: string) {
  return /<blockquote[\s>]/i.test(html) || /Messaggio inoltrato/i.test(html) || /ha scritto:/i.test(html);
}

type PendingAttachment = MailAttachmentPayload & { id: string; size: number };

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1] || "");
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function ComposerModal({
  accounts,
  defaultAccountId,
  maxAttachmentSizeMb = 35,
  mode,
  source,
  onClose,
  onSent,
}: {
  accounts: PublicMailAccount[];
  defaultAccountId?: string;
  maxAttachmentSizeMb?: number;
  mode: ComposeMode;
  source: MailMessageDetail | null;
  onClose: () => void;
  onSent: () => void;
}) {
  const accountIdInit = defaultAccountId || accounts[0]?.id || "";
  const selfEmails = useMemo(() => accounts.map((a) => a.email), [accounts]);
  const draft = useMemo(
    () => buildComposeDraft(mode, source, selfEmails),
    [mode, source, selfEmails],
  );

  const initialEditorHtml = useMemo(() => buildEditorInitialHtml(mode, source), [mode, source]);

  const [accountId, setAccountId] = useState(accountIdInit);
  const [to, setTo] = useState(draft.to);
  const [cc, setCc] = useState(draft.cc);
  const [bcc, setBcc] = useState(mode === "draft" && source?.bcc ? source.bcc : "");
  const [showCc, setShowCc] = useState(Boolean(draft.cc) || mode === "replyAll");
  const [showBcc, setShowBcc] = useState(mode === "draft" && Boolean(source?.bcc));
  const [subject, setSubject] = useState(draft.subject);
  const [bodyHtml, setBodyHtml] = useState(initialEditorHtml);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftStatus, setDraftStatus] = useState("");
  const [draftUid, setDraftUid] = useState<number | null>(
    mode === "draft" && source ? source.uid : null,
  );
  const [draftFolder, setDraftFolder] = useState<string | undefined>(
    mode === "draft" && source ? source.folder : undefined,
  );
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  const [suggestions, setSuggestions] = useState<Array<{ displayName: string; email: string }>>([]);
  const [suggestField, setSuggestField] = useState<"to" | "cc" | "bcc" | null>(null);
  const dirtyRef = useRef(true);
  const closingRef = useRef(false);
  const discardedRef = useRef(false);
  const bodyHtmlRef = useRef(bodyHtml);
  bodyHtmlRef.current = bodyHtml;
  const { confirm } = useConfirm();

  const composeHtml = useCallback(() => bodyHtmlRef.current || "<p></p>", []);

  const attachmentPayload = useCallback(
    (): MailAttachmentPayload[] =>
      attachmentsRef.current.map(({ filename, contentType, content }) => ({
        filename,
        contentType,
        content,
      })),
    [],
  );

  async function addAttachments(files: FileList | null) {
    if (!files?.length) return;
    const maxBytes = maxAttachmentSizeMb * 1024 * 1024;
    const next: PendingAttachment[] = [];
    for (const file of Array.from(files)) {
      if (file.size > maxBytes) {
        setError(`«${file.name}» supera il limite di ${maxAttachmentSizeMb} MB per allegato`);
        continue;
      }
      try {
        const content = await fileToBase64(file);
        next.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          filename: file.name,
          contentType: file.type || "application/octet-stream",
          content,
          size: file.size,
        });
      } catch {
        setError(`Impossibile leggere «${file.name}»`);
      }
    }
    if (next.length) {
      setAttachments((prev) => [...prev, ...next]);
      dirtyRef.current = true;
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function removeAttachment(id: string) {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
    dirtyRef.current = true;
  }

  const persistLocal = useCallback(() => {
    try {
      localStorage.setItem(
        draftStorageKey(accountId, mode, source),
        JSON.stringify({
          accountId,
          to,
          cc,
          bcc,
          subject,
          bodyHtml: bodyHtmlRef.current,
          mode,
          draftUid,
          draftFolder,
          at: Date.now(),
        }),
      );
    } catch {
      /* ignore quota */
    }
  }, [accountId, mode, source, to, cc, bcc, subject, draftUid, draftFolder]);

  const saveDraftNow = useCallback(
    async (reason: "manual" | "auto" | "open" | "close") => {
      if (!accountId || discardedRef.current) return false;
      const html = composeHtml();
      const text = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
      if (reason === "open" && mode === "new" && !to.trim() && !subject.trim() && text.length < 2) {
        return true;
      }
      setSavingDraft(true);
      if (reason !== "close") setDraftStatus("Salvataggio bozza…");
      persistLocal();
      try {
        if (discardedRef.current) return false;
        const res = await api.saveDraft({
          accountId,
          to: to.split(",").map((s) => s.trim()).filter(Boolean),
          cc: cc.split(",").map((s) => s.trim()).filter(Boolean),
          bcc: bcc.split(",").map((s) => s.trim()).filter(Boolean),
          subject,
          html,
          text,
          inReplyTo:
            mode === "forward" || mode === "new" || mode === "draft" ? undefined : source?.messageId || undefined,
          references:
            mode === "forward" || mode === "new" || mode === "draft" ? undefined : source?.messageId || undefined,
          replaceUid: draftUid || undefined,
          draftFolder,
          attachments: attachmentPayload(),
        });
        if (discardedRef.current) {
          // User discarded while save was in flight — remove the draft we just wrote
          if (res.uid && res.folder) {
            void api
              .bulk({
                action: "trash",
                items: [{ accountId, folder: res.folder, uid: res.uid }],
              })
              .catch(() => undefined);
          }
          return false;
        }
        if (res.uid) setDraftUid(res.uid);
        if (res.folder) setDraftFolder(res.folder);
        dirtyRef.current = false;
        const when = new Date().toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
        setDraftStatus(
          reason === "manual"
            ? `Bozza salvata nelle Bozze · ${when}`
            : reason === "close"
              ? `Bozza salvata · ${when}`
              : `Autosave bozza · ${when}`,
        );
        return true;
      } catch (e) {
        if (discardedRef.current) return false;
        setDraftStatus("Bozza salvata in locale (server non raggiungibile)");
        setError(e instanceof Error ? e.message : String(e));
        return false;
      } finally {
        setSavingDraft(false);
      }
    },
    [accountId, composeHtml, persistLocal, to, cc, bcc, subject, mode, source, draftUid, draftFolder, attachmentPayload],
  );

  // Restore local draft; always ensure quote is present for reply/forward
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftStorageKey(accountIdInit, mode, source));
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        bodyHtml?: string;
        to?: string;
        cc?: string;
        bcc?: string;
        subject?: string;
        draftUid?: number | null;
        draftFolder?: string;
      };
      if (parsed.bodyHtml && parsed.bodyHtml !== "<p></p>") {
        if (mode !== "new" && source && !bodyHasQuote(parsed.bodyHtml)) {
          setBodyHtml(`${parsed.bodyHtml}${buildQuotedBlock(mode, source)}`);
        } else {
          setBodyHtml(parsed.bodyHtml);
        }
      }
      if (parsed.to) setTo(parsed.to);
      if (parsed.cc) {
        setCc(parsed.cc);
        setShowCc(true);
      }
      if (parsed.bcc) {
        setBcc(parsed.bcc);
        setShowBcc(true);
      }
      if (parsed.subject) setSubject(parsed.subject);
      if (parsed.draftUid) setDraftUid(parsed.draftUid);
      if (parsed.draftFolder) setDraftFolder(parsed.draftFolder);
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      void saveDraftNow("open");
    }, 800);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = window.setInterval(() => {
      if (dirtyRef.current) void saveDraftNow("auto");
    }, 180_000);
    return () => window.clearInterval(t);
  }, [saveDraftNow]);

  useEffect(() => {
    dirtyRef.current = true;
  }, [accountId, to, cc, bcc, subject, bodyHtml, attachments]);

  useEffect(() => {
    const t = window.setInterval(() => persistLocal(), 20_000);
    return () => window.clearInterval(t);
  }, [persistLocal]);

  useEffect(() => {
    if (!suggestField) {
      setSuggestions([]);
      return;
    }
    const val = suggestField === "to" ? to : suggestField === "cc" ? cc : bcc;
    const q = val.split(",").pop()?.trim() || "";
    if (q.length < 2 || !accountId) {
      setSuggestions([]);
      return;
    }
    const handle = window.setTimeout(() => {
      void api.suggestContacts(q, accountId).then((r) => setSuggestions(r.contacts)).catch(() => setSuggestions([]));
    }, 180);
    return () => window.clearTimeout(handle);
  }, [to, cc, bcc, suggestField, accountId]);

  function applySuggestion(field: "to" | "cc" | "bcc", email: string) {
    const val = field === "to" ? to : field === "cc" ? cc : bcc;
    const parts = val.split(",");
    parts[parts.length - 1] = ` ${email}`;
    const next = parts.join(",").replace(/^\s+/, "");
    if (field === "to") setTo(next);
    else if (field === "cc") setCc(next);
    else setBcc(next);
    setSuggestions([]);
  }

  function suggestDropdown(field: "to" | "cc" | "bcc") {
    if (suggestField !== field || !suggestions.length) return null;
    return (
      <div className="suggest-list">
        {suggestions.map((s) => (
          <button
            key={s.email}
            type="button"
            className="suggest-item"
            onClick={() => applySuggestion(field, s.email)}
          >
            {s.displayName} · {s.email}
          </button>
        ))}
      </div>
    );
  }

  async function requestClose() {
    if (closingRef.current) return;
    closingRef.current = true;
    setDraftStatus("Salvo la bozza prima di chiudere…");
    const ok = await saveDraftNow("close");
    if (!ok && !discardedRef.current) {
      closingRef.current = false;
      const force = await confirm({
        title: "Bozza non salvata",
        message:
          "Non sono riuscito a salvare la bozza sul server (spesso per gli allegati). Chiudere comunque? Il messaggio potrebbe andare perso.",
        confirmLabel: "Chiudi comunque",
        cancelLabel: "Continua a scrivere",
        tone: "danger",
      });
      if (!force) {
        setDraftStatus("Bozza non salvata — continua a scrivere o riprova Salva bozza");
        return;
      }
    }
    onClose();
  }

  async function discardDraft() {
    if (closingRef.current) return;
    const hasContent =
      (bodyHtmlRef.current || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().length > 8 ||
      to.trim() ||
      subject.trim() ||
      attachmentsRef.current.length > 0;
    if (
      hasContent &&
      !(await confirm({
        title: "Scarta messaggio",
        message: "Scartare il messaggio? Non verrà salvato in bozza.",
        confirmLabel: "Scarta",
        cancelLabel: "Continua a scrivere",
        tone: "danger",
      }))
    ) {
      return;
    }
    discardedRef.current = true;
    closingRef.current = true;
    setDraftStatus("Scarto…");
    try {
      localStorage.removeItem(draftStorageKey(accountId, mode, source));
    } catch {
      /* ignore */
    }
    if (draftUid && draftFolder && accountId) {
      try {
        await api.bulk({
          action: "trash",
          items: [{ accountId, folder: draftFolder, uid: draftUid }],
        });
      } catch {
        /* draft may already be gone */
      }
    }
    onClose();
  }

  async function send() {
    if (!accountId) {
      setError("Scegli un account mittente");
      return;
    }
    const recipients = to.split(",").map((s) => s.trim()).filter(Boolean);
    if (!recipients.length) {
      setError("Inserisci almeno un destinatario in A");
      return;
    }
    setSending(true);
    setError("");
    const attList = attachmentPayload();
    const approxBytes = attList.reduce((n, a) => n + Math.ceil((a.content.length * 3) / 4), 0);
    if (approxBytes > 70 * 1024 * 1024) {
      setSending(false);
      setError("Allegati troppo pesanti per un unico invio. Riduci la dimensione o dividi i file.");
      return;
    }
    setDraftStatus(attList.length ? "Invio in corso (con allegati)…" : "Invio in corso…");
    try {
      const html = composeHtml();
      const text = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
      const sent = await api.send({
        accountId,
        to: recipients,
        cc: cc.split(",").map((s) => s.trim()).filter(Boolean),
        bcc: bcc.split(",").map((s) => s.trim()).filter(Boolean),
        subject,
        html,
        text,
        inReplyTo:
          mode === "forward" || mode === "new" || mode === "draft" ? undefined : source?.messageId || undefined,
        references:
          mode === "forward" || mode === "new" || mode === "draft" ? undefined : source?.messageId || undefined,
        sourceFolder:
          mode === "reply" || mode === "replyAll" ? source?.folder || undefined : undefined,
        sourceUid: mode === "reply" || mode === "replyAll" ? source?.uid || undefined : undefined,
        attachments: attList,
      });
      if (draftUid && draftFolder) {
        try {
          await api.bulk({
            action: "trash",
            items: [{ accountId, folder: draftFolder, uid: draftUid }],
          });
        } catch {
          /* draft may already be gone */
        }
      }
      try {
        localStorage.removeItem(draftStorageKey(accountId, mode, source));
      } catch {
        /* ignore */
      }
      if (sent.sentSaved === false) {
        setError(
          "Messaggio inviato, ma la copia in Inviate non è stata salvata sul server. Controlla la cartella Sent dell’account mittente.",
        );
        setDraftStatus("Inviato · copia Sent non salvata");
        // Keep composer open briefly so the warning is visible, then close on next action.
      }
      onSent();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(`Invio non riuscito: ${msg}`);
      setDraftStatus("Invio fallito — provo a salvare la bozza…");
      const saved = await saveDraftNow("manual");
      setDraftStatus(
        saved
          ? "Invio fallito · bozza salvata nelle Bozze"
          : "Invio fallito · bozza NON salvata sul server — non chiudere",
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="modal-back"
      onClick={() => {
        void requestClose();
      }}
    >
      <div className="modal modal-composer" onClick={(e) => e.stopPropagation()}>
        <div className="composer-chrome">
          <div>
            <div className="eyebrow">Compose</div>
            <strong className="composer-title">{draft.title}</strong>
            {draftStatus ? <div className="composer-draft-status">{draftStatus}</div> : null}
          </div>
          <div className="composer-chrome-actions">
            <button
              className="btn btn-draft"
              type="button"
              disabled={savingDraft || sending}
              onClick={() => void saveDraftNow("manual")}
            >
              {savingDraft ? "Salvo…" : "Salva bozza"}
            </button>
            <button
              className="btn btn-discard"
              type="button"
              disabled={savingDraft || sending}
              onClick={() => void discardDraft()}
              title="Chiudi senza salvare"
            >
              Scarta
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => void requestClose()}
              aria-label="Chiudi e salva bozza"
              type="button"
              disabled={savingDraft}
              title="Chiudi e salva bozza"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="composer-scroll">
          <div className="composer-fields">
            <div className="composer-field">
              <label className="label">Da</label>
              <select className="select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.email}
                  </option>
                ))}
              </select>
            </div>
            <div className="composer-field">
              <label className="label">A</label>
              <input
                className="input"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                onFocus={() => setSuggestField("to")}
                onBlur={() => window.setTimeout(() => setSuggestField((f) => (f === "to" ? null : f)), 150)}
                autoComplete="off"
                placeholder={mode === "forward" ? "Destinatario dell’inoltro" : undefined}
                autoFocus={mode === "forward"}
              />
              {suggestDropdown("to")}
            </div>
            <div className="composer-field-row">
              {!showCc ? (
                <button type="button" className="btn btn-ghost" onClick={() => setShowCc(true)}>
                  Cc
                </button>
              ) : (
                <div className="composer-field" style={{ flex: 1 }}>
                  <label className="label">Cc</label>
                  <input
                    className="input"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    onFocus={() => setSuggestField("cc")}
                    onBlur={() => window.setTimeout(() => setSuggestField((f) => (f === "cc" ? null : f)), 150)}
                    autoComplete="off"
                  />
                  {suggestDropdown("cc")}
                </div>
              )}
              <button type="button" className="btn btn-ghost" onClick={() => setShowBcc((v) => !v)}>
                {showBcc ? "Nascondi Ccn" : "Ccn"}
              </button>
            </div>
            {showBcc && (
              <div className="composer-field">
                <label className="label">Ccn</label>
                <input
                  className="input"
                  value={bcc}
                  onChange={(e) => setBcc(e.target.value)}
                  onFocus={() => setSuggestField("bcc")}
                  onBlur={() => window.setTimeout(() => setSuggestField((f) => (f === "bcc" ? null : f)), 150)}
                  autoComplete="off"
                />
                {suggestDropdown("bcc")}
              </div>
            )}
            <div className="composer-field">
              <label className="label">Oggetto</label>
              <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
          </div>

          <div className="composer-attach-bar">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="composer-file-input"
              onChange={(e) => void addAttachments(e.target.files)}
            />
            <button
              type="button"
              className="btn btn-ghost composer-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || savingDraft}
            >
              📎 Allega
            </button>
            <span className="composer-attach-hint">Max {maxAttachmentSizeMb} MB per file</span>
          </div>

          {attachments.length > 0 && (
            <ul className="composer-attachments">
              {attachments.map((a) => (
                <li key={a.id} className="composer-attachment">
                  <span className="composer-attachment-name" title={a.filename}>
                    📄 {a.filename}
                  </span>
                  <span className="composer-attachment-size">{formatBytes(a.size)}</span>
                  <button
                    type="button"
                    className="composer-attachment-remove"
                    onClick={() => removeAttachment(a.id)}
                    title="Rimuovi allegato"
                    aria-label={`Rimuovi ${a.filename}`}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}

          <RichMailEditor
            key={`${mode}-${source?.uid || "new"}-${source?.accountId || ""}-quote`}
            initialHtml={bodyHtml}
            onChange={setBodyHtml}
          />
        </div>

        <div className="composer-footer">
          <span className="composer-hint">
            ✕ / Chiudi = salva bozza · Scarta = elimina senza salvare
          </span>
          {error && <div className="error">{error}</div>}
          <div className="composer-actions">
            <button className="btn btn-discard" type="button" onClick={() => void discardDraft()} disabled={savingDraft || sending}>
              Scarta
            </button>
            <button className="btn" type="button" onClick={() => void requestClose()} disabled={savingDraft}>
              Chiudi
            </button>
            <button className="btn btn-primary" type="button" onClick={() => void send()} disabled={sending || savingDraft}>
              {sending ? "Invio…" : mode === "forward" ? "Inoltra" : "Invia"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
